import React from 'react';
import FollowupActions from './FollowupActions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Visas när kunden har accepterat ett av byggkonsultens tidsförslag
 * Scenario A1: KONSULT_PROPOSAL_ACCEPTED
 */
const KonsultProposalsAccepted = ({
  acceptedProposal,
  acceptedAt,
  bookedFollowupSlot,
  bookingSlot,
  estimatFollowup,
  onBookSlot,
  onSuggestTime,
  onBookMeeting,
  onApproveForCustomer,
  onBookHomeVisit,
  approveLoading,
  isApproved = false,
  onShowEstimatCustomerView,
  userRole,
  customerId,
}) => {
  const handleBookAcceptedSlot = async () => {
    try {
      const slot = acceptedProposal.displayText;
      onBookSlot.setBookingSlot(slot);

      const token = localStorage.getItem('token');

      if (!customerId) {
        onBookSlot.setBookingSlot('');
        onBookSlot.setResponseModalData({
          title: 'Kunde inte boka',
          message: 'Kund-ID saknas',
          type: 'error',
        });
        onBookSlot.setShowResponseModal(true);
        return;
      }

      const { date, time } = acceptedProposal;
      const response = await fetch(`${API_URL}/api/estimat/customer/${customerId}/followup`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date,
          time,
          displaySlot: slot,
          notes: 'Bokning från accepterat tidsförslag',
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Bokning misslyckades');
      }

      onBookSlot.setBookedFollowupSlot(slot);
      if (data?.meeting?.teamsJoinUrl) {
        onBookSlot.setFollowupTeamsUrl(data.meeting.teamsJoinUrl);
      }

      onBookSlot.setResponseModalData({
        title: 'Uppföljning bokad',
        message: 'En Teams‑inbjudan har skickats till kund, beställare 2 och byggkonsult.',
        type: 'success',
        details: data?.meeting?.teamsJoinUrl
          ? {
              'Teams-länk': (
                <a
                  href={data.meeting.teamsJoinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 underline"
                >
                  Öppna
                </a>
              ),
            }
          : null,
      });
      onBookSlot.setShouldRefreshAfterModal(true);
      onBookSlot.setShowResponseModal(true);
    } catch (err) {
      console.error(err);
      onBookSlot.setResponseModalData({
        title: 'Kunde inte boka',
        message: err.message || 'Okänt fel',
        type: 'error',
      });
      onBookSlot.setShowResponseModal(true);
    } finally {
      onBookSlot.setBookingSlot('');
    }
  };

  const isBooked = bookedFollowupSlot === acceptedProposal.displayText;
  const isBooking = bookingSlot === acceptedProposal.displayText;
  const isAlreadyBooked = bookedFollowupSlot || estimatFollowup?.selectedSlot || estimatFollowup?.meetingAt;
  const isCustomer = userRole === 'customer';

  // KUNDVY: Visa bekräftelse att de accepterat och väntar på bokning
  if (isCustomer) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-green-900 mb-1">
              Du har accepterat uppföljningsmöte
            </h4>
            <p className="text-sm text-green-700 mb-2">
              <strong>{acceptedProposal.displayText}</strong>
            </p>
            <p className="text-xs text-green-600">
              Byggkonsulten kommer att bekräfta bokningen och skicka en Teams-inbjudan till dig.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // BYGGKONSULTVY: Visa "Boka denna tid" knapp
  return (
    <div>
      <div className="text-sm text-gray-700 mb-4">
        <strong>Kunden accepterade ditt förslag</strong>
      </div>

      <ul className="space-y-2 text-sm text-gray-700 mb-4">
        <li className="flex items-center justify-between gap-4 bg-green-50 border border-green-200 rounded px-3 py-2">
          <span className="text-gray-900">{acceptedProposal.displayText}</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={`text-xs px-3 py-1.5 rounded whitespace-nowrap flex items-center gap-2 ${
                isBooked
                  ? 'bg-green-700 text-white opacity-80 cursor-not-allowed'
                  : isBooking
                  ? 'bg-green-600 text-white opacity-80 cursor-wait'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              title="Boka denna tid via Teams/Outlook"
              onClick={handleBookAcceptedSlot}
              disabled={isBooked || isBooking}
            >
              {isBooked ? (
                <>
                  <span>✓</span>
                  <span>Bokad</span>
                </>
              ) : isBooking ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  <span>Bokar…</span>
                </>
              ) : (
                'Boka denna tid'
              )}
            </button>
          </div>
        </li>
      </ul>

      <div className="text-xs text-green-600 mb-3">
        Accepterad {new Date(acceptedAt).toLocaleString('sv-SE')}
      </div>

      {/* Dölj knappar om uppföljning redan är bokad */}
      {!isAlreadyBooked && (
        <FollowupActions
          onSuggestTime={onSuggestTime}
          onBookMeeting={onBookMeeting}
          onApproveForCustomer={onApproveForCustomer}
          onBookHomeVisit={onBookHomeVisit}
          approveLoading={approveLoading}
          isApproved={isApproved}
        />
      )}

    </div>
  );
};

export default KonsultProposalsAccepted;
